import React, { useState } from 'react';
import { Topic } from '../../types/Topic';
import { SubjectType } from '../../types/Card';

interface TopicExtractorProps {
  extractedTopics: Topic[];
  onTopicsUpdated: (topics: Topic[]) => void;
}

const SUBJECT_OPTIONS: { value: SubjectType; label: string; color: string; emoji: string }[] = [
  { value: 'science', label: 'Science', color: '#4A90E2', emoji: '🔬' },
  { value: 'history', label: 'History', color: '#8B4513', emoji: '🏛️' },
  { value: 'geography', label: 'Geography', color: '#228B22', emoji: '🌍' },
  { value: 'maths', label: 'Mathematics', color: '#DC143C', emoji: '🔢' },
  { value: 'computing', label: 'Computing', color: '#FFD700', emoji: '💻' },
  { value: 'art', label: 'Art', color: '#FF8C00', emoji: '🎨' },
  { value: 'space', label: 'Space', color: '#9370DB', emoji: '🚀' }
];

export const TopicExtractor: React.FC<TopicExtractorProps> = ({ extractedTopics, onTopicsUpdated }) => {
  const [topics, setTopics] = useState<Topic[]>(extractedTopics);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTopic, setNewTopic] = useState({
    name: '',
    subject: 'science' as SubjectType,
    yearGroup: 5
  });

  const updateTopic = (index: number, updatedTopic: Partial<Topic>) => {
    const newTopics = [...topics];
    newTopics[index] = { ...newTopics[index], ...updatedTopic };
    setTopics(newTopics);
    onTopicsUpdated(newTopics);
  };

  const deleteTopic = (index: number) => {
    const topicName = topics[index].name;
    if (window.confirm(`Are you sure you want to delete the topic "${topicName}"?`)) {
      const newTopics = topics.filter((_, i) => i !== index);
      setTopics(newTopics);
      onTopicsUpdated(newTopics);
    }
  };

  const addTopic = () => {
    if (!newTopic.name.trim()) return;
    
    const topic: Topic = {
      name: newTopic.name.trim(),
      subject: newTopic.subject,
      yearGroup: newTopic.yearGroup,
      keyConcepts: [],
      confidence: 1.0 // Manual entry has full confidence
    };
    
    const newTopics = [...topics, topic];
    setTopics(newTopics);
    onTopicsUpdated(newTopics);
    
    // Reset form
    setNewTopic({
      name: '',
      subject: 'science',
      yearGroup: 5
    });
  };

  const getSubjectInfo = (subject: SubjectType) => {
    return SUBJECT_OPTIONS.find(option => option.value === subject) || SUBJECT_OPTIONS[0];
  };

  return (
    <div className="topic-extractor">
      <div className="extracted-topics">
        <h3>Extracted Topics</h3>
        {topics.length === 0 ? (
          <p className="no-topics">No topics found. Add topics manually below.</p>
        ) : (
          <div className="topics-list">
            {topics.map((topic, index) => {
              const subjectInfo = getSubjectInfo(topic.subject);
              const isEditing = editingId === `${index}`;
              
              return (
                <div key={index} className="topic-item" style={{ borderLeftColor: subjectInfo.color }}>
                  <div className="topic-header">
                    <span className="topic-emoji">{subjectInfo.emoji}</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={topic.name}
                        onChange={(e) => updateTopic(index, { name: e.target.value })}
                        onBlur={() => setEditingId(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') setEditingId(null);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        autoFocus
                        className="topic-name-input"
                        maxLength={100}
                      />
                    ) : (
                      <span 
                        className="topic-name"
                        onClick={() => setEditingId(`${index}`)}
                        title="Click to edit topic name"
                      >
                        {topic.name}
                      </span>
                    )}
                    <div className="topic-actions">
                      <button
                        onClick={() => deleteTopic(index)}
                        className="delete-button"
                        title="Delete topic"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  
                  <div className="topic-details">
                    <div className="topic-field">
                      <label>Subject:</label>
                      <select
                        value={topic.subject}
                        onChange={(e) => updateTopic(index, { subject: e.target.value as SubjectType })}
                        className="subject-select"
                      >
                        {SUBJECT_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.emoji} {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="topic-field">
                      <label>Year Group:</label>
                      <input
                        type="number"
                        min="1"
                        max="13"
                        value={topic.yearGroup || 5}
                        onChange={(e) => updateTopic(index, { yearGroup: parseInt(e.target.value) })}
                        className="year-input"
                      />
                    </div>
                    
                    {topic.confidence < 1.0 && (
                      <div className="confidence-indicator">
                        <span className="confidence-label">AI Confidence:</span>
                        <div className="confidence-bar">
                          <div 
                            className="confidence-fill"
                            style={{ width: `${topic.confidence * 100}%` }}
                          ></div>
                        </div>
                        <span className="confidence-value">{Math.round(topic.confidence * 100)}%</span>
                      </div>
                    )}
                  </div>
                  
                  {topic.keyConcepts && topic.keyConcepts.length > 0 && (
                    <div className="key-concepts">
                      <span className="concepts-label">Key Concepts:</span>
                      <div className="concepts-tags">
                        {topic.keyConcepts.map((concept, conceptIndex) => (
                          <span key={conceptIndex} className="concept-tag">
                            {concept}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      <div className="add-topic-section">
        <h4>Add Topic Manually</h4>
        <div className="add-topic-form">
          <div className="form-row">
            <input
              type="text"
              placeholder="Topic name (e.g., Forces and Motion)"
              value={newTopic.name}
              onChange={(e) => setNewTopic(prev => ({ ...prev, name: e.target.value }))}
              className="topic-name-input"
            />
            <select
              value={newTopic.subject}
              onChange={(e) => setNewTopic(prev => ({ ...prev, subject: e.target.value as SubjectType }))}
              className="subject-select"
            >
              {SUBJECT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.emoji} {option.label}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="1"
              max="13"
              value={newTopic.yearGroup}
              onChange={(e) => setNewTopic(prev => ({ ...prev, yearGroup: parseInt(e.target.value) }))}
              className="year-input"
              title="Year Group"
            />
            <button
              onClick={addTopic}
              disabled={!newTopic.name.trim()}
              className="add-button"
            >
              Add Topic
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};